import type { ComponentMultiStyleConfig } from "@chakra-ui/react"

// dist/css/badges.css and counter.css. A status is a dot and a word, never a tinted background.
const statusDot = (bg: string, extra: Record<string, unknown> = {}) => ({
  container: {
    bg: "transparent",
    color: "text",
    px: 0,
    gap: 2,
    fontSize: "2xs",
    _before: {
      content: '""',
      flex: "none",
      w: "9px",
      h: "9px",
      borderRadius: "full",
      bg,
      ...extra,
    },
  },
})

const counterPill = (bg: string, color: string) => ({
  container: {
    bg,
    color,
    minW: "20px",
    h: "20px",
    minH: "20px",
    px: "6px",
    py: 0,
    borderRadius: "full",
    fontSize: "3xs",
    fontWeight: "semibold",
    letterSpacing: "normal",
    fontVariantNumeric: "tabular-nums",
    justifyContent: "center",
  },
  label: {
    margin: "auto",
  },
})

export const Tag: ComponentMultiStyleConfig = {
  parts: ["container", "label", "closeButton", "leftIcon"],
  baseStyle: {
    container: {
      verticalAlign: "middle",
      fontFamily: "body",
      fontSize: "3xs",
      fontWeight: "semibold",
      letterSpacing: "wider",
      textTransform: "uppercase",
      borderRadius: "sm",
      border: "1px solid transparent",
      color: "text",
      bg: "surfaceGreige",
    },
    label: {
      lineHeight: "snug",
    },
  },
  variants: {
    // Badges
    default: {},
    outline: {
      container: { bg: "transparent", borderColor: "lineStrong" },
    },
    dark: {
      container: { bg: "surfaceInverse", color: "textInverse" },
    },
    accent: {
      container: { bg: "accent", color: "onAccent" },
    },
    // Existing variants mapped to the design system (ADR 0001, Q5): the per-component choice
    // between badge and status is made in lot 2.
    secondary: {
      container: { bg: "transparent", borderColor: "lineStrong" },
    },
    warning: statusDot("accent"),
    deepGrey: {
      container: { bg: "surfaceInverse", color: "textInverse" },
    },
    autocomplete: {
      container: {
        bg: "surface",
        borderColor: "lineStrong",
        fontSize: "xs",
        fontWeight: "normal",
        letterSpacing: "wide",
        textTransform: "none",
      },
    },
    // Counter (CountTag) — black pill; red is reserved for "action required" (counterAlert).
    round: counterPill("surfaceInverse", "textInverse"),
    // Statuses: filled black = settled, hollow = in progress, red = needs action, muted = closed.
    status: statusDot("surfaceInverse"),
    statusOpen: statusDot("surface", { boxShadow: "inset 0 0 0 2px black" }),
    statusAlert: statusDot("accent"),
    statusMuted: {
      container: {
        ...statusDot("lineStrong").container,
        color: "textMuted",
      },
    },
    // Counters
    counter: {
      container: {
        bg: "transparent",
        color: "textMuted",
        px: 0,
        fontSize: "xs",
        fontWeight: "normal",
        letterSpacing: "normal",
        textTransform: "none",
        fontVariantNumeric: "tabular-nums",
      },
    },
    counterSolid: counterPill("surfaceInverse", "textInverse"),
    counterAlert: counterPill("accent", "onAccent"),
  },
  sizes: {
    md: {
      container: {
        px: 3,
        py: "5px",
      },
    },
  },
  defaultProps: {
    size: "md",
    variant: "default",
  },
}
